import { Autocomplete, AutocompleteProps, TextField, Typography } from '@mui/material';
import { forwardRef, Ref, useState } from 'react';

import { HBTextFieldProps } from '../HBTextField/HBTextField';

export type HBAutocompleteProps<
  T,
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined,
  FreeSolo extends boolean | undefined,
> = Omit<AutocompleteProps<T, Multiple, DisableClearable, FreeSolo>, 'css' | 'ref'> & {
  renderInputProps?: HBTextFieldProps;
  dataTestId?: string;
  groupedTag?: boolean;
};

const HBAutoComplete = forwardRef(
  <
    T,
    Multiple extends boolean | undefined,
    DisableClearable extends boolean | undefined,
    FreeSolo extends boolean | undefined,
  >(
    {
      disableClearable = true,
      renderInput = (params) => <TextField {...params} />,
      dataTestId,
      groupedTag = false,
      ...props
    }: HBAutocompleteProps<T, Multiple, DisableClearable, FreeSolo>,
    ref?: Ref<HTMLSelectElement>,
  ) => {
    const [showGroupedTag, setShowGroupedTag] = useState<boolean>(false);
    const { onFocus, onBlur, title, ...restProps } = props;
    return (
      <Autocomplete<T, Multiple, DisableClearable, FreeSolo>
        ref={ref}
        {...(restProps.multiple && groupedTag
          ? {
              renderTags: (value) => {
                return (
                  showGroupedTag && (
                    <Typography
                      sx={{
                        ml: 2,
                        mr: 2,
                        color: (theme) => theme.palette.vendorPrimary.main,
                        position: 'absolute',
                      }}
                    >{`${title}: ${value.length} مورد`}</Typography>
                  )
                );
              },
            }
          : {})}
        renderInput={renderInput}
        onFocus={(e) => {
          setShowGroupedTag(false);
          onFocus?.(e);
        }}
        onBlur={(e) => {
          setShowGroupedTag(true);
          onBlur?.(e);
        }}
        blurOnSelect={true}
        disableClearable={(groupedTag || disableClearable) as DisableClearable}
        data-test-id={dataTestId}
        open={props.disabled ? false : props.open}
        {...restProps}
      />
    );
  },
);

HBAutoComplete.displayName = 'HBAutoComplete';

export default HBAutoComplete as <
  T,
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined,
  FreeSolo extends boolean | undefined,
>(
  props: HBAutocompleteProps<T, Multiple, DisableClearable, FreeSolo> & {
    ref?: Ref<HTMLFormElement>;
  },
) => JSX.Element;
